import { memo, useEffect, useMemo, type CSSProperties, type ReactNode } from "react";

// Components
import CardSpecimenSubmissionLogReportUX from "react-lib/apps/MEH/CardSpecimenSubmissionLogReportUX";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import {
  ACTIONS,
  BTN_ACTS,
  PickedState,
  State as ReportListSequence,
} from "react-lib/apps/MEH/sequence/MeReportList";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

export type CardSpecimenSubmissionLogReportProps = {
  //
  runSequence: any;
  setProp: any;
  onEvent: any;
  selectedDivision: Record<string, any>;

  // data
  ReportListSequence?: ReportListSequence["ReportListSequence"];

  // Common
  languageUX: "en" | "th";
  masterOptions?: Record<string, any[]>;
  masterData?: Record<string, any[]>;

  // layout จากหน้าที่นำไปใช้ (ไม่ส่ง = ค่าตั้งต้นของการ์ด)
  tableStyle?: CSSProperties;
  renderPagination?: (pagination: {
    page: number;
    pageCount: number;
    onChange: (page: number) => void;
  }) => ReactNode;
} & Pick<PickedState, "buttonLoadCheck" | "loadingStatus">;

const CardSpecimenSubmissionLogReport = (props: CardSpecimenSubmissionLogReportProps) => {
  useEffect(() => {
    props.runSequence({
      sequence: "MeReportList",
      restart: true,
    });
  }, [props.selectedDivision?.id]);

  const outlabOptions = useMemo(
    () => [{ key: "ALL", value: "ALL", text: "ALL" }, ...(props.masterOptions?.vendorList || [])],
    [props.masterOptions?.vendorList],
  );

  const reportListData = useMemo(() => {
    return (props.ReportListSequence?.reportList || []).map((item) => {
      const divisionHN = (item?.patient_hn || "").split("_").pop();
      // send_specimen_time เป็น null ได้ เมื่อยังไม่มีการบันทึกส่งสิ่งส่งตรวจ
      const sendDateTime = formatISOtoBEDateTime(item.send_specimen_time);

      return {
        ...item,
        collectSpecimenTime: (
          <div>{`${formatISOtoBEDateTime(item.collect_specimen_time)?.date} (${formatISOtoBEDateTime(item.collect_specimen_time)?.timeShort})`}</div>
        ),
        memberID: <div>{`${divisionHN} (${item.encounter_number})`}</div>,
        patientName: <div>{`${item.patient_name}`}</div>,
        specimenName: <div>{`${item.specimen_name}`}</div>,
        testNames: (
          <div>
            <ul>
              {item.test_names.map((name: string, index: number) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        ),
        outLab: <div>{item.vendor_name}</div>,
        specimenTime: (
          <div>{sendDateTime?.date ? `${sendDateTime.date} (${sendDateTime.timeShort})` : ""}</div>
        ),
        specimenUserName: <div>{item.send_specimen_user_name}</div>,
      };
    });
  }, [props.ReportListSequence?.reportList]);

  const handleChangeStartDate = (date: any) => {
    props.setProp("ReportListSequence.reportListFilter.start_date", date);
  };

  const handleChangeEndDate = (date: any) => {
    props.setProp("ReportListSequence.reportListFilter.end_date", date);
  };

  const handleChangeData = (_event: any, data: any) => {
    const { name, value, checked } = data;

    props.setProp(
      `ReportListSequence.reportListFilter.${name}`,
      checked !== undefined ? checked : value,
    );
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "MeReportList",
      action: ACTIONS.SEARCH,
    });
  };

  const handlePrint = () => {
    props.runSequence({
      sequence: "MeReportList",
      action: ACTIONS.PRINT,
    });
  };

  const handleDownloadXLSX = () => {
    props.runSequence({
      sequence: "MeReportList",
      action: ACTIONS.DOWNLOAD_XLSX,
    });
  };

  return (
    <>
      <CardSpecimenSubmissionLogReportUX
        reportListFilter={props.ReportListSequence?.reportListFilter}
        limit={props.ReportListSequence?.limit}
        currentPage={props.ReportListSequence?.currentPage}
        totalPage={props.ReportListSequence?.totalPage}
        reportListData={reportListData}
        onChangeStartDate={handleChangeStartDate}
        onChangeEndDate={handleChangeEndDate}
        onChangeData={handleChangeData}
        handlePageChange={(page: number) => {
          props.runSequence({
            sequence: "MeReportList",
            action: ACTIONS.SEARCH,
            currentPage: page,
          });
        }}
        tableStyle={props.tableStyle}
        pagination={props.renderPagination?.({
          page: (props.ReportListSequence?.currentPage ?? 0) + 1,
          pageCount: props.ReportListSequence?.totalPage ?? 1,
          onChange: (page: number) => {
            props.runSequence({
              sequence: "MeReportList",
              action: ACTIONS.SEARCH,
              currentPage: page - 1,
            });
          },
        })}
        handlePageSizeChange={(limit: number) => {
          props.runSequence({
            sequence: "MeReportList",
            action: ACTIONS.SEARCH,
            currentPage: 0,
            limit: limit,
          });
        }}
        outlabOptions={outlabOptions}
        patientQueueData={[]}
        buttonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={BTN_ACTS.SEARCH}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            // config
            style={{ background: "#2688D1", color: "white" }}
            fluid={true}
            size="small"
            title={"ค้นหา"}
          />
        }
        buttonPrint={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handlePrint}
            // data
            paramKey={BTN_ACTS.PRINT}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT]}
            // config
            style={{ background: "#8649E9", color: "white" }}
            fluid={true}
            size="small"
            title={"พิมพ์รายงาน"}
          />
        }
        buttonDownloadXlsx={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleDownloadXLSX}
            // data
            paramKey={BTN_ACTS.DOWNLOAD_XLSX}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.DOWNLOAD_XLSX]}
            // config
            color="green"
            fluid={true}
            size="small"
            title={"ดาวน์โหลด xlsx"}
          />
        }
      />
    </>
  );
};

export default memo(CardSpecimenSubmissionLogReport);
